import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Triangle, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";

const STATUS = {
  identified: { label: "Identified", cls: "bg-slate-100 text-slate-600" },
  discussing: { label: "Discussing", cls: "bg-amber-100 text-amber-700" },
  solved: { label: "Solved", cls: "bg-emerald-100 text-emerald-700" },
};
const ageDays = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
const plus7 = () => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
const fmtD = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "—");

export default function ManagementIssues() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("open");
  const [editing, setEditing] = useState(null);
  const [actForm, setActForm] = useState(null); // {solution, owner_id, owner_name, due_date}
  const [replacing, setReplacing] = useState(false);

  const { data } = useQuery({ queryKey: ["mgmt-issues"], queryFn: async () => (await api.get("/mgmt/issues")).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });
  const issues = tab === "open" ? data?.open || [] : data?.solved || [];
  const stats = data?.stats || {};
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mgmt-issues"] });
    qc.invalidateQueries({ queryKey: ["mgmt-todos"] });
    qc.invalidateQueries({ queryKey: ["daily-tasks"] });
    qc.invalidateQueries({ queryKey: ["activities"] });
  };

  const saveMut = useMutation({
    mutationFn: async (f) => {
      const payload = { title: f.title, detail: f.detail || null, status: f.status };
      if (f.id) return (await api.patch(`/mgmt/issues/${f.id}`, payload)).data;
      return (await api.post("/mgmt/issues", { title: f.title, detail: f.detail || null })).data;
    },
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Issue saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save issue"),
  });
  const voteMut = useMutation({
    mutationFn: async (id) => (await api.post(`/mgmt/issues/${id}/vote`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mgmt-issues"] }),
    onError: (e) => toast.error(e.response?.data?.detail || "Could not vote"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mgmt/issues/${id}`)).data,
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Issue deleted"); },
  });
  const actionMut = useMutation({
    mutationFn: async () => (await api.post(`/mgmt/issues/${editing.id}/action`, {
      solution: actForm.solution.trim(), owner_id: actForm.owner_id,
      owner_name: actForm.owner_name, due_date: actForm.due_date || null,
    })).data,
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Action assigned — the issue solves itself when it's ticked done"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not assign action"),
  });

  const openEdit = (i) => {
    setEditing({ ...i, detail: i.detail || "" });
    setActForm({ solution: i.solution || "", owner_id: "", owner_name: "", due_date: plus7() });
    setReplacing(false);
  };
  const setAct = (p) => setActForm((f) => ({ ...f, ...p }));

  return (
    <div data-testid="management-issues-page" className="pb-24">
      <PageHeader
        title="Issues (IDS)"
        subtitle={`Drop roadblocks & improvement ideas · Identify → Discuss → assign the Action · ${stats.open ?? 0} open · ${stats.solved_quarter ?? 0} solved this quarter`}
        actions={<Button onClick={() => { setEditing({ title: "", detail: "", status: "identified" }); setActForm(null); }} className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="drop-issue-btn"><Plus size={15} className="mr-1" />Drop an Issue</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-10">
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4 items-start">
        <Card className="rounded-sm border-slate-200 shadow-none overflow-hidden" data-testid="issues-table">
          <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
            <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden">
              {["open", "solved"].map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`issues-tab-${t}`}
                  className={`px-3 py-1 text-xs font-semibold capitalize ${tab === t ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{t}</button>
              ))}
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{tab === "open" ? "sorted by votes" : "most recent first"}</span>
          </div>
          {issues.length === 0 ? (
            <div className="px-4 py-8 text-sm text-slate-400" data-testid="issues-empty">No {tab} issues.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="text-left px-4 py-2 font-semibold w-16">#</th>
                  <th className="text-left px-2 py-2 font-semibold">Issue</th>
                  <th className="text-left px-2 py-2 font-semibold">Raised by</th>
                  <th className="text-left px-2 py-2 font-semibold">Age</th>
                  <th className="text-left px-2 py-2 font-semibold">Votes</th>
                  <th className="text-left px-2 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((i, idx) => (
                  <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openEdit(i)} data-testid={`issue-row-${i.id}`}>
                    <td className="px-4 py-2.5 font-mono text-cyan-700 text-xs whitespace-nowrap" data-testid={`issue-number-${i.id}`}>{i.number || idx + 1}</td>
                    <td className="px-2 py-2.5">
                      <div className="font-semibold text-slate-900">{i.title}</div>
                      {tab === "solved" && i.solution && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">Solved → {i.solution}</div>}
                      {tab === "open" && i.action && (
                        <div className="flex items-center gap-1 text-xs mt-0.5" data-testid={`issue-action-line-${i.id}`}>
                          <ArrowRight size={11} className="text-cyan-600 shrink-0" />
                          <span className="text-slate-600 line-clamp-1">{i.action.title}</span>
                          <span className="text-slate-400 whitespace-nowrap">· {i.action.owner_name} · {i.action.status === "done" ? "✓ done" : `due ${fmtD(i.action.due_date)}`}</span>
                        </div>
                      )}
                      {tab === "open" && !i.action && i.detail && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{i.detail}</div>}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-slate-600 whitespace-nowrap">{i.raised_by}{i.source_metric_id ? " ⚙" : ""}</td>
                    <td className="px-2 py-2.5 font-mono text-xs text-slate-500">{ageDays(i.created_at)}d</td>
                    <td className="px-2 py-2.5">
                      {tab === "open" ? (
                        <button data-testid={`vote-btn-${i.id}`} onClick={(e) => { e.stopPropagation(); voteMut.mutate(i.id); }}
                          className={`inline-flex items-center gap-1 font-mono text-xs px-2 py-1 rounded-sm border ${i.my_vote ? "border-cyan-600 text-cyan-700 bg-cyan-50" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
                          <Triangle size={9} className={i.my_vote ? "fill-cyan-600 text-cyan-600" : ""} />{i.vote_count}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-mono text-xs text-slate-400"><Triangle size={9} />{i.vote_count}</span>
                      )}
                    </td>
                    <td className="px-2 py-2.5">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${STATUS[i.status]?.cls}`} data-testid={`issue-status-${i.id}`}>{STATUS[i.status]?.label}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <Card className="rounded-sm border-slate-200 shadow-none p-4" data-testid="ids-workflow-card">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">IDS Workflow</div>
          {[["I", "Identify", "State the real issue, not the symptom. Owner restates it in one line."],
            ["D", "Discuss", "Everyone says their piece once. No politicking — facts on the table."],
            ["S", "Solve", "Agree the action and assign it to a person — it lands in their Daily Tasks titled with the issue number. The issue flips to Solved the moment that to-do is ticked done."]].map(([n, t, d]) => (
            <div key={n} className="flex gap-3 mb-3">
              <div className="w-7 h-7 bg-slate-800 text-white font-mono text-xs flex items-center justify-center rounded-sm flex-shrink-0">{n}</div>
              <div><div className="font-semibold text-sm text-slate-900">{t}</div><div className="text-xs text-slate-500">{d}</div></div>
            </div>
          ))}
          <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-3 mt-1 flex gap-2"><AlertCircle size={14} className="flex-shrink-0 mt-0.5" />Scorecard metrics that miss their goal two weeks running drop in here automatically (marked ⚙).</div>
        </Card>
      </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto" data-testid="issue-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? `Issue ${editing.number || ""}` : "Drop an Issue"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">What's the issue? *</label>
                <Input value={editing.title} onChange={(e) => setEditing((f) => ({ ...f, title: e.target.value }))} className="rounded-sm mt-1" data-testid="issue-title-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Detail</label>
                <Textarea value={editing.detail} rows={2} onChange={(e) => setEditing((f) => ({ ...f, detail: e.target.value }))} className="rounded-sm mt-1" data-testid="issue-detail-input" />
              </div>
              {editing.id && (
                <>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Status</label>
                    <Select value={editing.status} onValueChange={(v) => setEditing((f) => ({ ...f, status: v }))}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="issue-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="identified">Identified</SelectItem>
                        <SelectItem value="discussing">Discussing</SelectItem>
                        <SelectItem value="solved" disabled>Solved — via the action to-do</SelectItem>
                      </SelectContent>
                    </Select>
                    {editing.status !== "solved" && (
                      <p className="text-[11px] text-slate-400 mt-1">Issues flip to Solved automatically when their action to-do is completed.</p>
                    )}
                  </div>
                  {editing.status === "solved" && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-sm" data-testid="issue-solved-card">
                      <div className="flex items-center gap-1.5 text-emerald-700 font-semibold text-xs uppercase tracking-wider"><CheckCircle2 size={13} />Solved</div>
                      {editing.solution && <div className="text-slate-700 mt-1">{editing.solution}</div>}
                      <p className="text-[11px] text-slate-400 mt-1">Set status back to Discussing to reopen.</p>
                    </div>
                  )}
                  {editing.status === "discussing" && actForm && (
                    <div className="border-t border-slate-200 pt-3" data-testid="issue-action-section">
                      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Solution / Action</div>
                      {editing.action && !replacing ? (
                        <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid="issue-action-card">
                          <div className="text-sm font-medium text-slate-800">{editing.action.title}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {editing.action.owner_name} · due {fmtD(editing.action.due_date)} ·{" "}
                            <span className={editing.action.status === "done" ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"}>
                              {editing.action.status === "done" ? "done" : "open"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-1.5">
                            <p className="text-[10.5px] text-slate-400">In {editing.action.owner_name}'s Daily Tasks as "{editing.number} — …" — ticking it done solves this issue.</p>
                            <button type="button" className="text-[10px] text-slate-400 hover:text-cyan-700 uppercase tracking-wider shrink-0 ml-2"
                              onClick={() => { setReplacing(true); setAct({ solution: editing.action.title }); }} data-testid="replace-action-btn">Replace</button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Textarea rows={2} placeholder="Action agreed — this becomes the to-do" value={actForm.solution}
                            onChange={(e) => setAct({ solution: e.target.value })} className="rounded-sm" data-testid="action-solution-input" />
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={actForm.owner_id} onValueChange={(v) => {
                              const m = (team || []).find((t) => t.id === v);
                              setAct({ owner_id: v, owner_name: m?.name || "" });
                            }}>
                              <SelectTrigger className="rounded-sm" data-testid="action-owner-select"><SelectValue placeholder="Assign to *" /></SelectTrigger>
                              <SelectContent>{(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input type="date" value={actForm.due_date} onChange={(e) => setAct({ due_date: e.target.value })} className="rounded-sm" data-testid="action-due-input" />
                          </div>
                          <Button className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="assign-action-btn"
                            disabled={!actForm.solution.trim() || !actForm.owner_id || actionMut.isPending}
                            onClick={() => actionMut.mutate()}>
                            <ArrowRight size={14} className="mr-1" />Assign action to-do
                          </Button>
                          <p className="text-[10.5px] text-slate-400">Lands in the owner's Daily Tasks as "{editing.number || "ISS-…"} — …" with this due date. The issue solves itself when it's ticked done.</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {editing?.id ? (
              <Button variant="ghost" className="text-red-600 hover:bg-red-50 rounded-sm" data-testid="delete-issue-btn"
                onClick={() => window.confirm("Delete this issue (and its action to-do)?") && delMut.mutate(editing.id)}><Trash2 size={15} className="mr-1" />Delete</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)} data-testid="cancel-issue-btn">Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="save-issue-btn"
                disabled={!editing?.title?.trim() || saveMut.isPending} onClick={() => saveMut.mutate(editing)}>
                {editing?.id ? "Save" : "Drop Issue"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
